import { ArrowCounterClockwiseIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { useDetectedMeters } from "@/api/media-transmitters/queries";
import type { DetectedMeter } from "@/api/media-transmitters/types";
import { DetectedMetersBulkActions } from "@/components/media-transmitters/detected-meters-bulk-actions";
import { ResetFramesAlertDialog } from "@/components/media-transmitters/reset-frames-alert-dialog";
import {
	type Column,
	MeterTable,
	usePagination,
} from "@/components/meters/meter-table";
import { Button } from "@/components/ui/button";
import { EmptyCell } from "@/components/ui/empty-cell";
import { isValidLang, type Lang } from "@/config/routes";
import { useDebounce } from "@/hooks/use-debounce";
import { useMeterSelection } from "@/hooks/use-meter-selection";
import { useResetMeterFlow } from "@/hooks/use-reset-meter-flow";
import { formatDateTime } from "@/utils/format-datetime";

interface DetectedMetersTableProps {
	sn: string;
}

export function DetectedMetersTable({ sn }: DetectedMetersTableProps) {
	const { t } = useTranslation();
	const { lang } = useParams<{ lang: string }>();
	const currentLang: Lang = lang && isValidLang(lang) ? lang : "pl";

	const { page, setPage } = usePagination();
	const [pageSize, setPageSize] = useState(20);
	const [ordering, setOrdering] = useState("");
	const [search, setSearch] = useState("");
	const debouncedSearch = useDebounce(search);
	const resetFlow = useResetMeterFlow(sn);

	const { data, isLoading, isError, refetch } = useDetectedMeters(sn, {
		page,
		page_size: pageSize,
		search: debouncedSearch || undefined,
		ordering: ordering || undefined,
	});

	const rows = data?.results ?? [];
	const {
		selectedSerials: selectedIds,
		toggleRow,
		toggleAll,
		isAllSelected,
		isIndeterminate,
		clearSelection,
	} = useMeterSelection(
		rows,
		[sn, page, pageSize, debouncedSearch, ordering],
		(row: DetectedMeter) => row.id,
	);

	const columns: Column<DetectedMeter>[] = [
		{
			key: "id",
			label: t("mediaTransmitters.detectedColId"),
			sortField: "id",
		},
		{
			key: "manufacturer",
			label: t("mediaTransmitters.detectedColManufacturer"),
			sortField: "manufacturer",
		},
		{
			key: "model",
			label: t("mediaTransmitters.detectedColModel"),
			sortField: "model",
		},
		{
			key: "frames",
			label: t("mediaTransmitters.colFrames"),
			sortField: "frames",
		},
		{
			key: "rssi",
			label: t("mediaTransmitters.colRssi"),
			sortField: "rssi",
			render: (row) => `${row.rssi} dBm`,
		},
		{
			key: "last_message",
			label: t("mediaTransmitters.detectedColLastMessage"),
			sortField: "last_message",
			render: (row) =>
				row.last_message ? (
					formatDateTime(row.last_message, currentLang)
				) : (
					<EmptyCell />
				),
		},
		{
			key: "_actions",
			label: "",
			render: (row) => (
				<Button
					variant="ghost"
					size="sm"
					onClick={() => resetFlow.open(row.id)}
				>
					<ArrowCounterClockwiseIcon
						aria-hidden="true"
						className="mr-2 h-4 w-4"
					/>
					{t("mediaTransmitters.actionReset")}
				</Button>
			),
		},
	];

	return (
		<>
			<MeterTable
				data={data}
				columns={columns}
				loading={isLoading}
				isError={isError}
				onRetry={() => refetch()}
				page={page}
				pageSize={pageSize}
				onPageChange={(p) => setPage(p)}
				onPageSizeChange={(s) => {
					setPageSize(s);
					setPage(1);
				}}
				search={search}
				onSearchChange={(s) => {
					setSearch(s);
					setPage(1);
				}}
				searchPlaceholder={t("mediaTransmitters.searchPlaceholder")}
				ordering={ordering}
				onOrderingChange={(o) => {
					setOrdering(o);
					setPage(1);
				}}
				rowKeyField="id"
				selectedKeys={selectedIds}
				onSelectKey={toggleRow}
				onSelectAll={toggleAll}
				isAllSelected={isAllSelected}
				isIndeterminate={isIndeterminate}
				selectionBar={
					<DetectedMetersBulkActions
						sn={sn}
						selectedIds={selectedIds}
						onSuccess={clearSelection}
						onClear={clearSelection}
					/>
				}
			/>
			<ResetFramesAlertDialog
				open={resetFlow.pendingMeterId !== null}
				onOpenChange={(o) => !o && resetFlow.close()}
				pending={resetFlow.isPending}
				scope="meter"
				target={resetFlow.pendingMeterId ?? ""}
				onConfirm={resetFlow.confirm}
			/>
		</>
	);
}
